import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Search, Pencil, Trash2, AlertTriangle, MapPin, CalendarClock, Eye } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { parseApiError } from '../lib/parseApiError';
import { formatDate } from '../lib/formatDate';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIPI_IMPIANTO = ['Registratore di cassa', 'Stampante fiscale', 'Gestionale', 'POS', 'Bilancia', 'Altro'];

export default function ImpiantiPage() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [impianti, setImpianti] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [puntiVendita, setPuntiVendita] = useState([]);
  const [allPuntiVendita, setAllPuntiVendita] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtroCitta, setFiltroCitta] = useState('');
  const [filtroScadenza, setFiltroScadenza] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingImpianto, setEditingImpianto] = useState(null);
  
  // Client search
  const [clienteSearch, setClienteSearch] = useState('');
  const [clienteSearchResults, setClienteSearchResults] = useState([]);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [zone, setZone] = useState([]);
  const [filtroZona, setFiltroZona] = useState('');
  
  const [formData, setFormData] = useState({
    cliente_id: '',
    punto_vendita_id: '',
    tipo: '',
    marca: '',
    modello: '',
    matricola: '',
    zona: '',
    data_installazione: '',
    data_scadenza_verifica: '',
    note: '',
    // Abbonamento
    abbonamento_attivo: false,
    abbonamento_descrizione: '',
    abbonamento_data_inizio: '',
    abbonamento_data_scadenza: '',
    abbonamento_importo: ''
  });

  useEffect(() => {
    fetchData();
    fetchZone();
  }, []);
  
  const fetchZone = async () => {
    try {
      const res = await axios.get(`${API}/zone`);
      setZone(res.data);
    } catch (error) {
      console.error('Errore caricamento zone:', error);
    }
  };

  const fetchData = async () => {
    try {
      const [impiantiRes, clientiRes, puntiRes] = await Promise.all([
        axios.get(`${API}/impianti`),
        axios.get(`${API}/clienti`),
        axios.get(`${API}/punti-vendita`)
      ]);
      setImpianti(impiantiRes.data);
      setClienti(clientiRes.data);
      setAllPuntiVendita(puntiRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  // Search cliente
  const searchClienti = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setClienteSearchResults([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/clienti/search?q=${encodeURIComponent(query)}`);
      setClienteSearchResults(response.data);
    } catch (error) {
      console.error('Search error:', error);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      searchClienti(clienteSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [clienteSearch, searchClienti]);

  const handleSelectCliente = async (cliente) => {
    setSelectedCliente(cliente);
    setClienteSearch(cliente.ragione_sociale);
    setClienteSearchResults([]);
    setFormData({ ...formData, cliente_id: cliente.id, punto_vendita_id: '' });
    
    try {
      const puntiRes = await axios.get(`${API}/punti-vendita?cliente_id=${cliente.id}`);
      setPuntiVendita(puntiRes.data);
    } catch (error) {
      console.error('Error loading punti vendita:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { 
        cliente_id: formData.cliente_id,
        punto_vendita_id: formData.punto_vendita_id || null,
        tipo: formData.tipo,
        marca: formData.marca,
        modello: formData.modello,
        matricola: formData.matricola,
        data_installazione: formData.data_installazione || null,
        data_scadenza_verifica: formData.data_scadenza_verifica || null,
        note: formData.note || null
      };
      if (!data.punto_vendita_id) delete data.punto_vendita_id;
      
      let impiantoId = null;
      
      if (editingImpianto) {
        await axios.put(`${API}/impianti/${editingImpianto.id}`, data);
        impiantoId = editingImpianto.id;
        toast.success('Impianto aggiornato');
      } else {
        const response = await axios.post(`${API}/impianti`, data);
        impiantoId = response.data.id;
        toast.success('Impianto creato');
        
        // Crea abbonamento se richiesto (solo per nuovi impianti)
        if (formData.abbonamento_attivo && formData.abbonamento_data_inizio && formData.abbonamento_data_scadenza) {
          try {
            await axios.post(`${API}/abbonamenti`, {
              cliente_id: formData.cliente_id,
              punto_vendita_id: formData.punto_vendita_id || null,
              impianto_id: impiantoId,
              descrizione: formData.abbonamento_descrizione || `Assistenza ${formData.tipo} - ${formData.matricola}`,
              data_inizio: formData.abbonamento_data_inizio,
              data_scadenza: formData.abbonamento_data_scadenza,
              importo: formData.abbonamento_importo ? parseFloat(formData.abbonamento_importo) : 0,
              attivo: true
            });
            toast.success('Abbonamento attivato');
          } catch (abbError) {
            toast.error('Impianto creato, ma errore nella creazione abbonamento');
          }
        }
      }
      
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(parseApiError(error, 'Errore nel salvataggio'));
    }
  };

  const handleEdit = async (impianto) => {
    setEditingImpianto(impianto);
    
    // Find cliente
    const cliente = clienti.find(c => c.id === impianto.cliente_id);
    if (cliente) {
      setSelectedCliente(cliente);
      setClienteSearch(cliente.ragione_sociale);
      
      // Load punti vendita for this cliente
      try {
        const puntiRes = await axios.get(`${API}/punti-vendita?cliente_id=${cliente.id}`);
        setPuntiVendita(puntiRes.data);
      } catch (error) {
        console.error('Error loading punti vendita:', error);
      }
    }
    
    setFormData({
      cliente_id: impianto.cliente_id,
      punto_vendita_id: impianto.punto_vendita_id || '',
      tipo: impianto.tipo || '',
      marca: impianto.marca || '',
      modello: impianto.modello || '',
      matricola: impianto.matricola,
      zona: impianto.zona || '',
      data_installazione: impianto.data_installazione || '',
      data_scadenza_verifica: impianto.data_scadenza_verifica || '',
      note: impianto.note || '',
      // Reset abbonamento fields for edit
      abbonamento_attivo: false,
      abbonamento_descrizione: '',
      abbonamento_data_inizio: '',
      abbonamento_data_scadenza: '',
      abbonamento_importo: ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo impianto?')) return;
    try {
      await axios.delete(`${API}/impianti/${id}`);
      toast.success('Impianto eliminato');
      fetchData();
    } catch (error) {
      toast.error(parseApiError(error, 'Errore eliminazione'));
    }
  };

  const resetForm = () => {
    setEditingImpianto(null);
    setSelectedCliente(null);
    setClienteSearch('');
    setClienteSearchResults([]);
    setPuntiVendita([]);
    setFormData({
      cliente_id: '',
      punto_vendita_id: '',
      tipo: '',
      marca: '',
      modello: '',
      matricola: '',
      zona: '',
      data_installazione: '',
      data_scadenza_verifica: '',
      note: '',
      abbonamento_attivo: false,
      abbonamento_descrizione: '',
      abbonamento_data_inizio: '',
      abbonamento_data_scadenza: '',
      abbonamento_importo: ''
    });
  };

  const getClienteName = (clienteId) => {
    const cliente = clienti.find(c => c.id === clienteId);
    return cliente?.ragione_sociale || '-';
  };

  const getClienteCitta = (clienteId) => {
    const cliente = clienti.find(c => c.id === clienteId);
    return cliente?.citta_legale || '';
  };

  const getPuntoVenditaName = (puntoId) => {
    const punto = allPuntiVendita.find(p => p.id === puntoId);
    return punto ? `${punto.nome} - ${punto.citta}` : '-';
  };

  const isExpiringSoon = (dateStr) => {
    if (!dateStr) return false;
    const date = new Date(dateStr);
    const today = new Date();
    const diff = (date - today) / (1000 * 60 * 60 * 24);
    return diff >= 0 && diff <= 30;
  };

  const isExpired = (dateStr) => {
    if (!dateStr) return false;
    const date = new Date(dateStr);
    const today = new Date();
    return date < today;
  };

  // Estrai lista città uniche dai clienti
  const cittaUniche = [...new Set(clienti.map(c => c.citta_legale).filter(Boolean))].sort();

  const filteredImpianti = impianti.filter(i => {
    const matchSearch = 
      i.matricola?.toLowerCase().includes(search.toLowerCase()) ||
      i.tipo?.toLowerCase().includes(search.toLowerCase()) ||
      i.marca?.toLowerCase().includes(search.toLowerCase()) ||
      getClienteName(i.cliente_id)?.toLowerCase().includes(search.toLowerCase());
    
    const clienteCitta = getClienteCitta(i.cliente_id);
    const matchCitta = !filtroCitta || clienteCitta === filtroCitta;
    
    // Filtro zona
    const matchZona = !filtroZona || i.zona === filtroZona;
    
    // Filtro scadenza
    let matchScadenza = true;
    if (filtroScadenza === 'scaduti') {
      matchScadenza = isExpired(i.data_scadenza_verifica);
    } else if (filtroScadenza === 'in_scadenza') {
      matchScadenza = isExpiringSoon(i.data_scadenza_verifica);
    } else if (filtroScadenza === 'validi') {
      matchScadenza = i.data_scadenza_verifica && !isExpired(i.data_scadenza_verifica) && !isExpiringSoon(i.data_scadenza_verifica);
    }
    
    return matchSearch && matchCitta && matchZona && matchScadenza;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground font-mono text-sm">CARICAMENTO...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="impianti-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight">Impianti</h1>
          <p className="text-muted-foreground text-sm mt-1">{impianti.length} impianti registrati</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="rounded-none font-mono uppercase tracking-wider" data-testid="new-impianto-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nuovo Impianto
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-2xl rounded-none">
            <DialogHeader>
              <DialogTitle className="font-mono uppercase tracking-wider">
                {editingImpianto ? 'Modifica Impianto' : 'Nuovo Impianto'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Cliente Search */}
              <div className="relative">
                <Label className="text-xs font-mono uppercase">Cliente * (cerca per nome o P.IVA)</Label>
                <Input
                  value={clienteSearch}
                  onChange={(e) => {
                    setClienteSearch(e.target.value);
                    if (selectedCliente && !editingImpianto) {
                      setSelectedCliente(null);
                      setFormData({ ...formData, cliente_id: '', punto_vendita_id: '' });
                      setPuntiVendita([]);
                    }
                  }}
                  placeholder="Digita ragione sociale o P.IVA..."
                  className="rounded-none"
                  required={!selectedCliente}
                  disabled={!!editingImpianto}
                  data-testid="impianto-cliente-search"
                />
                {clienteSearchResults.length > 0 && !selectedCliente && (
                  <div className="absolute z-50 w-full mt-1 bg-background border border-border shadow-lg max-h-48 overflow-y-auto">
                    {clienteSearchResults.map((c) => (
                      <div
                        key={c.id}
                        className="p-3 hover:bg-accent cursor-pointer border-b border-border last:border-b-0"
                        onClick={() => handleSelectCliente(c)}
                      >
                        <p className="font-medium">{c.ragione_sociale}</p>
                        <p className="text-xs text-muted-foreground">P.IVA: {c.partita_iva || '-'}</p>
                      </div>
                    ))}
                  </div>
                )}
                {selectedCliente && (
                  <p className="text-xs text-success mt-1">Cliente: {selectedCliente.ragione_sociale}</p>
                )}
              </div>

              {/* Punto Vendita */}
              {selectedCliente && (
                <div>
                  <Label className="text-xs font-mono uppercase flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    Punto Vendita (indirizzo installazione)
                  </Label>
                  <Select 
                    value={formData.punto_vendita_id || "none"} 
                    onValueChange={(value) => setFormData({...formData, punto_vendita_id: value === "none" ? "" : value})}
                  >
                    <SelectTrigger className="rounded-none" data-testid="impianto-punto-vendita">
                      <SelectValue placeholder="Seleziona punto vendita" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      <SelectItem value="none">Non assegnato</SelectItem>
                      {puntiVendita.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.nome} - {p.indirizzo}, {p.citta}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {puntiVendita.length === 0 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Nessun punto vendita. Creane uno dalla pagina cliente.
                    </p>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-mono uppercase">Tipo *</Label>
                  <Select 
                    value={formData.tipo} 
                    onValueChange={(value) => setFormData({...formData, tipo: value})}
                  >
                    <SelectTrigger className="rounded-none" data-testid="impianto-tipo">
                      <SelectValue placeholder="Seleziona tipo" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      {TIPI_IMPIANTO.map((tipo) => (
                        <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase">Matricola *</Label>
                  <Input
                    value={formData.matricola}
                    onChange={(e) => setFormData({...formData, matricola: e.target.value})}
                    className="rounded-none font-mono"
                    required
                    data-testid="impianto-matricola"
                  />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase">Marca</Label>
                  <Input
                    value={formData.marca}
                    onChange={(e) => setFormData({...formData, marca: e.target.value})}
                    className="rounded-none"
                    data-testid="impianto-marca"
                  />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase">Modello</Label>
                  <Input
                    value={formData.modello}
                    onChange={(e) => setFormData({...formData, modello: e.target.value})}
                    className="rounded-none"
                    data-testid="impianto-modello"
                  />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase">Data Installazione</Label>
                  <Input
                    type="date"
                    value={formData.data_installazione}
                    onChange={(e) => setFormData({...formData, data_installazione: e.target.value})}
                    className="rounded-none"
                    data-testid="impianto-data-installazione"
                  />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase">Scadenza Verifica Periodica</Label>
                  <Input
                    type="date"
                    value={formData.data_scadenza_verifica}
                    onChange={(e) => setFormData({...formData, data_scadenza_verifica: e.target.value})}
                    className="rounded-none"
                    data-testid="impianto-scadenza"
                  />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase">Zona</Label>
                  <Select 
                    value={formData.zona || 'none'} 
                    onValueChange={(v) => setFormData({...formData, zona: v === 'none' ? '' : v})}
                  >
                    <SelectTrigger className="rounded-none" data-testid="impianto-zona">
                      <SelectValue placeholder="Seleziona zona" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none max-h-60">
                      <SelectItem value="none">-- Nessuna zona --</SelectItem>
                      {zone.map((z) => (
                        <SelectItem key={z.id} value={z.nome}>{z.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs font-mono uppercase">Note</Label>
                  <Textarea
                    value={formData.note}
                    onChange={(e) => setFormData({...formData, note: e.target.value})}
                    className="rounded-none"
                    data-testid="impianto-note"
                  />
                </div>
              </div>
              
              {/* Sezione Abbonamento - solo per nuovi impianti */}
              {!editingImpianto && (
                <div className="border border-border p-4 mt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Checkbox
                      id="abbonamento-attivo"
                      checked={formData.abbonamento_attivo}
                      onCheckedChange={(checked) => setFormData({...formData, abbonamento_attivo: checked})}
                      data-testid="impianto-abbonamento-check"
                    />
                    <Label htmlFor="abbonamento-attivo" className="text-sm font-medium flex items-center gap-2 cursor-pointer">
                      <CalendarClock className="h-4 w-4" />
                      Attiva abbonamento assistenza
                    </Label>
                  </div>
                  
                  {formData.abbonamento_attivo && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-6">
                      <div className="md:col-span-2">
                        <Label className="text-xs font-mono uppercase">Descrizione Abbonamento</Label>
                        <Input
                          value={formData.abbonamento_descrizione}
                          onChange={(e) => setFormData({...formData, abbonamento_descrizione: e.target.value})}
                          className="rounded-none"
                          placeholder={`Assistenza ${formData.tipo || 'impianto'} - ${formData.matricola || ''}`}
                          data-testid="abbonamento-descrizione"
                        />
                      </div>
                      <div>
                        <Label className="text-xs font-mono uppercase">Data Inizio *</Label>
                        <Input
                          type="date"
                          value={formData.abbonamento_data_inizio}
                          onChange={(e) => setFormData({...formData, abbonamento_data_inizio: e.target.value})}
                          className="rounded-none"
                          data-testid="abbonamento-data-inizio"
                        />
                      </div>
                      <div>
                        <Label className="text-xs font-mono uppercase">Data Scadenza *</Label>
                        <Input
                          type="date"
                          value={formData.abbonamento_data_scadenza}
                          onChange={(e) => setFormData({...formData, abbonamento_data_scadenza: e.target.value})}
                          className="rounded-none"
                          data-testid="abbonamento-data-scadenza"
                        />
                      </div>
                      <div>
                        <Label className="text-xs font-mono uppercase">Importo (EUR)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.abbonamento_importo}
                          onChange={(e) => setFormData({...formData, abbonamento_importo: e.target.value})}
                          className="rounded-none"
                          placeholder="0.00"
                          data-testid="abbonamento-importo"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none">
                  Annulla
                </Button>
                <Button type="submit" className="rounded-none font-mono uppercase" disabled={!selectedCliente} data-testid="save-impianto-btn">
                  {editingImpianto ? 'Aggiorna' : 'Crea'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Cerca impianto..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 rounded-none"
            data-testid="search-impianti"
          />
        </div>
        <Select value={filtroCitta || "all"} onValueChange={(v) => setFiltroCitta(v === "all" ? "" : v)}>
          <SelectTrigger className="w-full md:w-48 rounded-none" data-testid="filter-citta-impianti">
            <SelectValue placeholder="Tutte le città" />
          </SelectTrigger>
          <SelectContent className="rounded-none max-h-60">
            <SelectItem value="all">Tutte le città</SelectItem>
            {cittaUniche.map((citta) => (
              <SelectItem key={citta} value={citta}>{citta}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filtroScadenza || "all"} onValueChange={(v) => setFiltroScadenza(v === "all" ? "" : v)}>
          <SelectTrigger className="w-full md:w-48 rounded-none" data-testid="filter-scadenza-impianti">
            <SelectValue placeholder="Tutte le scadenze" />
          </SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="all">Tutte le scadenze</SelectItem>
            <SelectItem value="scaduti">Scaduti</SelectItem>
            <SelectItem value="in_scadenza">In scadenza (30gg)</SelectItem>
            <SelectItem value="validi">Validi</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filtroZona || "all"} onValueChange={(v) => setFiltroZona(v === "all" ? "" : v)}>
          <SelectTrigger className="w-full md:w-48 rounded-none" data-testid="filter-zona-impianti">
            <SelectValue placeholder="Tutte le zone" />
          </SelectTrigger>
          <SelectContent className="rounded-none max-h-60">
            <SelectItem value="all">Tutte le zone</SelectItem>
            {zone.map((z) => (
              <SelectItem key={z.id} value={z.nome}>{z.nome}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="border border-border overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr>
              <th className="bg-primary/10">Matricola</th>
              <th className="bg-purple-600/10">Tipo</th>
              <th className="hidden md:table-cell bg-cyan-600/10">Marca/Modello</th>
              <th className="bg-blue-600/10">Cliente</th>
              <th className="hidden lg:table-cell bg-emerald-600/10">Punto Vendita</th>
              <th className="bg-amber-600/10">Scadenza Verifica</th>
              <th>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {filteredImpianti.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center text-muted-foreground py-8">
                  Nessun impianto trovato
                </td>
              </tr>
            ) : (
              filteredImpianti.map((impianto) => (
                <tr 
                  key={impianto.id} 
                  className="hover:bg-muted transition-colors cursor-pointer"
                  onClick={() => navigate(`/impianti/${impianto.id}`)}
                >
                  <td className="font-mono font-bold bg-primary/5 border-l-4 border-l-primary">{impianto.matricola}</td>
                  <td className="bg-purple-600/5 text-purple-600 dark:text-purple-400 border-l-4 border-l-purple-600">{impianto.tipo}</td>
                  <td className="hidden md:table-cell bg-cyan-600/5 text-cyan-600 dark:text-cyan-400 border-l-4 border-l-cyan-600">{impianto.marca} {impianto.modello}</td>
                  <td className="bg-blue-600/5 text-blue-600 dark:text-blue-400 border-l-4 border-l-blue-600">{getClienteName(impianto.cliente_id)}</td>
                  <td className="hidden lg:table-cell bg-emerald-600/5 border-l-4 border-l-emerald-600">
                    {impianto.punto_vendita_id ? (
                      <span className="flex items-center gap-1 text-sm text-emerald-600 dark:text-emerald-400">
                        <MapPin className="h-3 w-3" />
                        {getPuntoVenditaName(impianto.punto_vendita_id)}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </td>
                  <td className="bg-amber-600/5 border-l-4 border-l-amber-600">
                    <div className="flex items-center gap-2">
                      <Badge 
                        variant="outline" 
                        className={`rounded-none font-mono text-xs ${isExpiringSoon(impianto.data_scadenza_verifica) ? 'border-warning text-warning' : ''}`}
                      >
                        {formatDate(impianto.data_scadenza_verifica)}
                      </Badge>
                      {isExpiringSoon(impianto.data_scadenza_verifica) && (
                        <AlertTriangle className="h-4 w-4 text-warning" />
                      )}
                    </div>
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-1">
                      <Link to={`/impianti/${impianto.id}`}>
                        <Button variant="ghost" size="icon" className="h-8 w-8 rounded-none" title="Dettagli e Storico" data-testid={`view-impianto-${impianto.id}`}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      </Link>
                      {isAdmin() && (
                        <>
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-none" onClick={() => handleEdit(impianto)} data-testid={`edit-impianto-${impianto.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-none text-destructive" onClick={() => handleDelete(impianto.id)} data-testid={`delete-impianto-${impianto.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
